/**
 * ExportModal Component
 * Modal for selecting export options and triggering PDF generation
 */

import React, { useState, useEffect } from 'react';
import { FiX, FiDownload, FiFileText, FiClock, FiUser, FiList, FiLoader, FiCheck, FiEyeOff, FiPaperclip, FiAlertTriangle } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

// Export type configurations
const EXPORT_TYPES = {
  case: [
    {
      id: 'case_summary',
      name: 'Case Summary',
      description: 'Concise overview with completed actions, documents, and risk assessment',
      icon: FiFileText,
      recommended: true,
    },
    {
      id: 'case_timeline',
      name: 'Case Timeline',
      description: 'Full chronological history of all case events',
      icon: FiClock,
    },
  ],
  staff: [
    {
      id: 'staff_timeline',
      name: 'Staff Timeline',
      description: 'Complete history across all cases for this staff member',
      icon: FiUser,
    },
  ],
  workflow: [
    {
      id: 'workflow',
      name: 'Workflow Documentation',
      description: 'Full workflow with steps, notes, and audit log',
      icon: FiList,
    },
  ],
};

/**
 * ExportModal Component
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onExport - Export handler (receives exportType, options)
 * @param {string} props.entityType - 'case' | 'staff' | 'workflow'
 * @param {string} props.entityName - Name/ID of entity being exported
 * @param {boolean} props.isLoading - Whether export is in progress
 */
const ExportModal = ({
  isOpen,
  onClose,
  onExport,
  entityType = 'case',
  entityName = '',
  isLoading = false,
  documents = [], // Case documents for attachment option
}) => {
  const [selectedType, setSelectedType] = useState(null);
  const [completedOnly, setCompletedOnly] = useState(true);
  const [anonymize, setAnonymize] = useState(false);
  const [includeDocuments, setIncludeDocuments] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);

  // Calculate document stats for UI
  // Check for various document type formats (MIME types, extensions, or form values)
  // Note: Upload form stores 'pdf', 'image', 'doc' - not MIME types
  const isSupportedType = (docType) => {
    if (!docType) return false;
    const type = docType.toLowerCase();
    return (
      type === 'application/pdf' ||
      type === 'pdf' ||
      type === 'image/jpeg' ||
      type === 'image/jpg' ||
      type === 'jpeg' ||
      type === 'jpg' ||
      type === 'image/png' ||
      type === 'png' ||
      type === 'image' ||  // From upload form dropdown
      type.includes('pdf') ||
      type.includes('jpeg') ||
      type.includes('jpg') ||
      type.includes('png')
    );
  };
  const supportedDocs = documents.filter(doc => isSupportedType(doc.document_type));
  const hasDocuments = supportedDocs.length > 0;
  const docCount = Math.min(supportedDocs.length, 10);
  const totalSize = supportedDocs.reduce((sum, doc) => sum + (doc.file_size || 0), 0);
  const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(1);

  const exportTypes = EXPORT_TYPES[entityType] || [];

  // Set default selection on open
  useEffect(() => {
    if (isOpen && exportTypes.length > 0) {
      const recommended = exportTypes.find((t) => t.recommended);
      setSelectedType(recommended?.id || exportTypes[0].id);
      setExportSuccess(false);
      setAnonymize(false);
      setIncludeDocuments(false);
      setExportProgress(null);
    }
  }, [isOpen, entityType]);

  const handleExport = async () => {
    if (!selectedType) return;

    try {
      await onExport(selectedType, {
        completedOnly: selectedType === 'case_summary' ? completedOnly : false,
        anonymize,
        includeDocuments: includeDocuments && hasDocuments,
        onProgress: (progress) => setExportProgress(progress),
      });
      setExportSuccess(true);
      setExportProgress(null);

      // Auto-close after success
      setTimeout(() => {
        onClose();
        setExportSuccess(false);
      }, 1500);
    } catch (error) {
      console.error('Export failed:', error);
      setExportProgress(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-white">Export Document</h2>
            <p className="text-blue-100 text-sm mt-1">
              {entityName ? `Exporting: ${entityName}` : 'Select export options'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
            disabled={isLoading}
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Export Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Export Type
            </label>
            <div className="space-y-3">
              {exportTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setSelectedType(type.id)}
                  disabled={isLoading}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${selectedType === type.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-start space-x-3">
                    <div
                      className={`p-2 rounded-lg ${selectedType === type.id ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
                        }`}
                    >
                      <SafeIcon icon={type.icon} className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900">{type.name}</span>
                        {type.recommended && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                            Recommended
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{type.description}</p>
                    </div>
                    {selectedType === type.id && (
                      <div className="text-blue-500">
                        <SafeIcon icon={FiCheck} className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Filter Options */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-4">
            {/* Completed Only - only for case_summary */}
            {selectedType === 'case_summary' && (
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={completedOnly}
                  onChange={(e) => setCompletedOnly(e.target.checked)}
                  disabled={isLoading}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div>
                  <span className="font-medium text-gray-700">Completed actions only</span>
                  <p className="text-sm text-gray-500">
                    Only include actions that have been marked as completed
                  </p>
                </div>
              </label>
            )}

            {/* Anonymization Option - available for all export types */}
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={anonymize}
                onChange={(e) => setAnonymize(e.target.checked)}
                disabled={isLoading}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-700">Anonymize names</span>
                  <SafeIcon icon={FiEyeOff} className="w-4 h-4 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">
                  Replace names with initials (e.g., Sarah Johnson → SJ)
                </p>
              </div>
            </label>

            {/* Include Documents Option - only for case exports */}
            {entityType === 'case' && (
              <label className={`flex items-center space-x-3 ${hasDocuments ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
                <input
                  type="checkbox"
                  checked={includeDocuments}
                  onChange={(e) => setIncludeDocuments(e.target.checked)}
                  disabled={isLoading || !hasDocuments}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-700">Include attached documents</span>
                    <SafeIcon icon={FiPaperclip} className="w-4 h-4 text-gray-400" />
                  </div>
                  {hasDocuments ? (
                    <p className="text-sm text-gray-500">
                      Append {docCount} document{docCount !== 1 ? 's' : ''} ({totalSizeMB} MB) to the export
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400">
                      No attachable documents (PDF, JPEG, PNG supported)
                    </p>
                  )}
                </div>
              </label>
            )}

            {/* Warning for large exports */}
            {includeDocuments && totalSize > 10 * 1024 * 1024 && (
              <div className="flex items-start space-x-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 text-amber-600 mt-0.5" />
                <p className="text-sm text-amber-700">
                  Large export ({totalSizeMB} MB). This may take a moment to generate.
                </p>
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-start space-x-3">
              <SafeIcon icon={FiFileText} className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-900">PDF Export</p>
                <p className="text-blue-700 mt-1">
                  The document will be generated as a professional PDF with headers, footers, and
                  page numbers. It will be marked as CONFIDENTIAL.
                </p>
              </div>
            </div>
          </div>

          {/* Progress indicator */}
          {exportProgress && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center space-x-3 mb-2">
                <SafeIcon icon={FiLoader} className="w-4 h-4 text-blue-600 animate-spin" />
                <span className="font-medium text-blue-800">
                  {exportProgress.message || 'Processing...'}
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${exportProgress.percent || 0}%` }}
                />
              </div>
              {exportProgress.currentDoc && (
                <p className="text-xs text-blue-600 mt-1">
                  Current: {exportProgress.currentDoc}
                </p>
              )}
            </div>
          )}

          {/* Success Message */}
          {exportSuccess && (
            <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center space-x-3">
                <div className="bg-green-500 text-white p-1 rounded-full">
                  <SafeIcon icon={FiCheck} className="w-4 h-4" />
                </div>
                <span className="font-medium text-green-800">
                  Export completed! Your download should start automatically.
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-end space-x-3 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={!selectedType || isLoading || exportSuccess}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isLoading ? (
              <>
                <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                <span>Generating...</span>
              </>
            ) : exportSuccess ? (
              <>
                <SafeIcon icon={FiCheck} className="w-4 h-4" />
                <span>Downloaded</span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiDownload} className="w-4 h-4" />
                <span>Export PDF</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
